import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { EntrepriseSettings } from '../../mocks/entreprise';
import { useBranding } from '../../context/BrandingContext';

interface UserInfo { id: string; nom: string; role: string; }

export default function ParametresEntreprise() {
  const navigate = useNavigate();
  const { refreshBranding } = useBranding();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [form, setForm] = useState<EntrepriseSettings>({
    nom: '', slogan: '', adresse: '', telephone: '', email: '', 
    logoUrl: '', headerText: '', footerText: '', nif: '', rccm: '', 
    devise: 'FCFA', couleurPrimaire: '#4a7c59'
  });
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    const token = localStorage.getItem('atelier_bassoum_token');
    if (stored && token) {
      setUser(JSON.parse(stored));
      fetchSettings(token);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchSettings = async (token: string) => {
    try {
      const data = await api.getSettings(token);
      // Normalize data to avoid shadow keys (snake_case vs camelCase)
      const normalized: EntrepriseSettings = {
        nom: data.nom || '',
        slogan: data.slogan || '',
        adresse: data.adresse || '',
        telephone: data.telephone || '',
        email: data.email || '',
        logoUrl: data.logo_url || data.logoUrl || '',
        headerText: data.header_text || data.headerText || '',
        footerText: data.footer_text || data.footer_text || '',
        nif: data.nif || '',
        rccm: data.rccm || '',
        devise: data.devise || 'FCFA',
        couleurPrimaire: data.couleur_primaire || data.couleurPrimaire || '#4a7c59',
        clientObligatoireVente: data.client_obligatoire_vente || 0
      };
      setForm(normalized);
      setLogoPreview(normalized.logoUrl || '');
    } catch (err) {
      console.error(err);
      showToast('Erreur lors du chargement des paramètres', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Preview local
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    // Upload server
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    
    setUploading(true);
    try {
      const res = await api.uploadImage(token, file);
      // Store only the path part/filename to be consistent
      const filename = res.url.split('/').pop();
      setForm(p => ({ ...p, logoUrl: filename || '' }));
      setLogoPreview(res.url); // Initial state for immediate preview
      showToast('Logo mis en ligne. Cliquez sur Enregistrer pour confirmer.');
    } catch (err) {
      console.error(err);
      showToast('Erreur d\'envoi du logo', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.updateSettings(token, form);
      localStorage.setItem('atelier_bassoum_entreprise', JSON.stringify(form));
      await refreshBranding(); // Update context globally
      setSaved(true);
      showToast('Paramètres sauvegardés avec succès !');
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error(err);
      showToast('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleBackup = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    setMaintenanceLoading(true);
    try {
      await api.backupDatabase(token);
      showToast('Sauvegarde effectuée avec succès !');
    } catch (err) {
      console.error(err);
      showToast('Erreur lors de la sauvegarde', 'error');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleReset = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    const confirm1 = window.confirm("ATTENTION : Cette action va supprimer toutes les commandes, ventes et transactions financières pour repartir à zéro. La liste des clients et du stock sera conservée. Voulez-vous continuer ?");
    if (!confirm1) return;

    const confirm2 = window.confirm("Êtes-vous ABSOLUMENT sûr ? Cette action est irréversible. Une sauvegarde sera tentée avant la purge.");
    if (!confirm2) return;

    setMaintenanceLoading(true);
    try {
      // Automatic backup before reset
      try { await api.backupDatabase(token); } catch(e) { console.warn("Auto-backup failed, but proceeding..."); }
      
      const res = await api.resetDatabase(token);
      showToast(res.message || 'Réinitialisation terminée !', 'success');
      // Refresh to see clean state
      setTimeout(() => window.location.reload(), 2000);
    } catch (err: any) {
      console.error(err);
      showToast(err.message || 'Erreur lors de la réinitialisation', 'error');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const COULEURS = [
    { value: '#4a7c59', label: 'Vert forêt' },
    { value: '#8b5e3c', label: 'Terre' },
    { value: '#c5972e', label: 'Or' },
    { value: '#d4724a', label: 'Corail' },
    { value: '#2e7d8b', label: 'Teal' },
  ];

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-playfair font-bold text-xl text-gray-900">Identité de l'entreprise</h2>
            <p className="text-xs text-gray-400 mt-0.5">Paramètres utilisés sur les factures et reçus</p>
          </div>
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${saved ? 'bg-foret-100 text-foret-700' : 'bg-foret-500 text-white hover:bg-foret-600'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={saved ? 'ri-check-line' : 'ri-save-line'} />
            </div>
            {saved ? 'Enregistré !' : 'Enregistrer'}
          </button>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Logo + Couleur */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-800 mb-5 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-foret-500"><i className="ri-store-2-line" /></div>
                Logo & Identité visuelle
              </h3>
              <div className="flex items-start gap-6 mb-5">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50">
                    {logoPreview ? (
                      <img src={api.getProxyUrl(logoPreview)} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center text-gray-300">
                        <i className="ri-image-line text-3xl" />
                      </div>
                    )}
                  </div>
                  <label className="text-xs text-foret-600 font-semibold cursor-pointer hover:underline whitespace-nowrap">
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoFile} disabled={uploading} />
                    {uploading ? 'Envoi...' : 'Changer le logo'}
                  </label>
                  <p className="text-[9px] text-gray-400 text-center leading-tight">
                    Optimisé automatiquement<br/>pour mobile & bureau
                  </p>
                  {logoPreview && !uploading && (
                    <button onClick={() => { setLogoPreview(''); setForm(p => ({ ...p, logoUrl: '' })); }} className="text-xs text-red-400 cursor-pointer hover:underline whitespace-nowrap">Supprimer</button>
                  )}
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <label htmlFor="entreprise-nom" className="block text-xs font-semibold text-gray-700 mb-1">Nom de l'entreprise *</label>
                    <input id="entreprise-nom" type="text" value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))} placeholder="Nom de votre entreprise" title="Nom officiel de l'entreprise" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400" />
                  </div>
                  <div>
                    <label htmlFor="entreprise-slogan" className="block text-xs font-semibold text-gray-700 mb-1">Slogan</label>
                    <input id="entreprise-slogan" type="text" value={form.slogan} onChange={e => setForm(p => ({ ...p, slogan: e.target.value }))} placeholder="ex: L'élégance africaine, sur mesure" title="Slogan de l'entreprise" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Couleur principale</label>
                <div className="flex gap-3 flex-wrap">
                  {COULEURS.map(c => (
                     <button
                      key={c.value}
                      onClick={() => setForm(p => ({ ...p, couleurPrimaire: c.value }))}
                      title={`Utiliser la couleur ${c.label}`}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 cursor-pointer transition-all text-xs font-medium ${form.couleurPrimaire === c.value ? 'border-gray-700 scale-105' : 'border-transparent bg-gray-50 hover:bg-gray-100'}`}
                    >
                       <style>{`
                         .color-swatch-${c.label.replace(/\s+/g, '-')} {
                           background-color: ${c.value};
                         }
                       `}</style>
                       <span className={`w-4 h-4 rounded-full flex-shrink-0 color-swatch-${c.label.replace(/\s+/g, '-')}`} />
                       {c.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Coordonnées */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-800 mb-5 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-foret-500"><i className="ri-map-pin-line" /></div>
                Coordonnées
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label htmlFor="entreprise-adresse" className="block text-xs font-semibold text-gray-700 mb-1">Adresse complète</label>
                  <input id="entreprise-adresse" type="text" value={form.adresse} onChange={e => setForm(p => ({ ...p, adresse: e.target.value }))} placeholder="Adresse du siège" title="Adresse complète" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400" />
                </div>
                <div>
                  <label htmlFor="entreprise-tel" className="block text-xs font-semibold text-gray-700 mb-1">Téléphone</label>
                  <input id="entreprise-tel" type="text" value={form.telephone} onChange={e => setForm(p => ({ ...p, telephone: e.target.value }))} placeholder="+223 ..." title="Numéro de téléphone" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400" />
                </div>
                <div>
                  <label htmlFor="entreprise-email" className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                  <input id="entreprise-email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="contact@entreprise.com" title="Adresse email de contact" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400" />
                </div>
                <div>
                  <label htmlFor="entreprise-nif" className="block text-xs font-semibold text-gray-700 mb-1">NIF</label>
                  <input id="entreprise-nif" type="text" value={form.nif} onChange={e => setForm(p => ({ ...p, nif: e.target.value }))} placeholder="Numéro d'Identification Fiscale" title="NIF de l'entreprise" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400" />
                </div>
                <div>
                  <label htmlFor="entreprise-rccm" className="block text-xs font-semibold text-gray-700 mb-1">RCCM</label>
                  <input id="entreprise-rccm" type="text" value={form.rccm} onChange={e => setForm(p => ({ ...p, rccm: e.target.value }))} placeholder="Registre du Commerce" title="RCCM de l'entreprise" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400" />
                </div>
              </div>
            </div>

            {/* Préférences Métier */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-800 mb-5 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-foret-500"><i className="ri-settings-5-line" /></div>
                Préférences de vente
              </h3>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div>
                  <p className="text-sm font-bold text-gray-800">Nom du client obligatoire</p>
                  <p className="text-xs text-gray-500">Force le personnel à renseigner le nom du client lors d&apos;une vente de tissu</p>
                </div>
                <button 
                  onClick={() => setForm(p => ({ ...p, clientObligatoireVente: p.clientObligatoireVente ? 0 : 1 }))}
                  title="Activer/Désactiver le nom du client obligatoire"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${form.clientObligatoireVente ? 'bg-foret-500' : 'bg-gray-200'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.clientObligatoireVente ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            {/* Maintenance & Sécurité */}
            <div className="bg-white rounded-2xl border border-red-100 p-6">
              <h3 className="font-semibold text-red-600 mb-5 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-red-500"><i className="ri-shield-flash-line" /></div>
                Maintenance & Sécurité
              </h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-gray-800">Sauvegarde complète</p>
                    <p className="text-xs text-gray-500">Télécharger toutes les données (JSON) pour archivage externe.</p>
                  </div>
                  <button 
                    onClick={handleBackup}
                    disabled={maintenanceLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:border-gray-300 rounded-xl text-xs font-bold text-gray-700 transition-all shadow-sm disabled:opacity-50"
                  >
                    <i className="ri-download-cloud-2-line" />
                    {maintenanceLoading ? 'Traitement...' : 'Sauvegarder'}
                  </button>
                </div>

                <div className="p-4 bg-red-50 rounded-xl border border-red-100 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-red-800">Réinitialisation de la base</p>
                    <p className="text-xs text-red-600">Supprimer commandes/ventes et restaurer le stock. **Action irréversible.**</p>
                  </div>
                  <button 
                    onClick={handleReset}
                    disabled={maintenanceLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-xl text-xs font-bold text-white transition-all shadow-md shadow-red-200 disabled:opacity-50"
                  >
                    <i className="ri-refresh-line" />
                    {maintenanceLoading ? 'Purge en cours...' : 'Réinitialiser'}
                  </button>
                </div>
              </div>
            </div>

            {/* Preview hint */}
            <div className="bg-foret-50 border border-foret-100 rounded-xl px-4 py-3 flex items-start gap-2">
              <div className="w-4 h-4 flex items-center justify-center text-foret-600 flex-shrink-0 mt-0.5"><i className="ri-information-line text-sm" /></div>
              <p className="text-xs text-foret-700">Ces paramètres s&apos;appliquent à toutes les factures générées depuis les commandes et les ventes de tissu. Pour prévisualiser, générez une facture depuis une commande ou une vente.</p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
